import type {
  StageExplainerExtraData,
} from '../../../models/end-client-reporting-component-types/stage-explainer';
import type {
  StageMatrixExtraData,
} from '../../../models/end-client-reporting-component-types/stage-matrix';

/**
 * What the two authored modules start with when somebody adds one by hand.
 *
 * Seeded rather than empty because neither has a shape of its own until something is in it: an empty
 * card is a blank rectangle with nothing to react to, where a filled one shows what the module is
 * for and gets edited into place.
 *
 * **These are examples, not defaults anyone should ship.** The words are a brand relationship ladder
 * because that is the deck both modules were first built for; a study about anything else needs
 * every one of them replaced. A generated design never sees them -- the agent writes its own from
 * the survey in front of it.
 *
 * Handed out through functions rather than as the constants themselves: one shared object given to
 * every block on a deck means editing one silently edits the rest.
 */
/** The band both modules carry, so a ladder and its explainer cannot start out disagreeing. */
const SPECTRUM = [
  'Advocacy & equity',
  'Consideration & intent',
  'Familiarity & perception',
  'Risk & erosion',
];

const EXPLAINER: StageExplainerExtraData = {
  panels: [
    {
      title: 'Brand Core',
      parts: [
        {
          heading: 'Promoters',
          points: [
            'Actively recommending',
            'High repeat usage and trust',
            'Defend brand against competitors',
          ],
        },
        {
          heading: 'Loyalists',
          points: [
            'Default to you when needed',
            'Low price sensitivity',
            'Positive but mostly private',
          ],
        },
      ],
    },
    {
      title: 'Active Market',
      parts: [
        {
          heading: 'Trialists',
          points: [
            'Recently worked with you',
            'Comparing future options',
            'Experience determines next move',
          ],
        },
        {
          heading: 'Prospects',
          points: [
            'Open to switching',
            'Limited equity given to brands',
            'Influenceable by messaging and proof',
          ],
        },
      ],
    },
    {
      title: 'Market Aware',
      parts: [
        {
          heading: 'Informed',
          points: [
            'Understand what you offer',
            'Have formed early opinions',
            'Not yet in decision mode',
          ],
        },
        {
          heading: 'Recognizers',
          points: [
            'Name or logo feels familiar',
            'Limited understanding of services',
            'No emotional connection',
          ],
        },
      ],
    },
    {
      title: 'Equity Deficit',
      parts: [
        {
          heading: 'Unaware',
          points: ['No exposure', 'No opinion', 'No impact'],
        },
        {
          heading: 'Detractors',
          points: [
            'Actively discourage others from using you',
            'Share negative experiences publicly',
          ],
        },
      ],
    },
  ],
  spectrum: SPECTRUM,
};

/**
 * The stage names, the spectrum and the awareness column of the same ladder.
 *
 * The stages assume eight rungs in four pairs, which is the shape that deck's question had. Pointed
 * at a shorter scale they are clamped to the rungs that exist -- see the block -- which is the right
 * failure for an example: visibly wrong rather than quietly wrong, and renamed in a minute.
 */
/**
 * The example's prose, keyed by the rung it describes.
 *
 * A ladder switching its explainer on gets these for any rung it shares a name with, so the deck
 * this feature was built against comes up written rather than as eight empty panels. A ladder with
 * its own rung names gets blanks, which is the right answer -- prose invented for a scale nobody
 * recognises is worse than a gap, and the headings are there to write against.
 */
const LADDER_STAGES = [
  { label: 'Brand Core', rungs: 2 },
  { label: 'Active Market', rungs: 2 },
  { label: 'Market Aware', rungs: 2 },
  { label: 'Equity Deficit', rungs: 2 },
];

/**
 * The stage names of the example ladder, in order, so a stage added by hand comes up named.
 *
 * A ladder is almost always this shape -- four stages down a relationship scale -- and typing the
 * fourth name every time is the sort of chore that ends with two decks calling it different things.
 * Past the end of this list a new stage is unnamed, which is the honest answer: nothing knows what
 * a fifth one would be called.
 */
export const EXAMPLE_STAGE_LABELS: string[] = LADDER_STAGES.map(stage => stage.label);

export const EXAMPLE_PART_POINTS: Record<string, string[]> = Object.fromEntries(
  EXPLAINER.panels.flatMap(panel =>
    (panel.parts ?? []).map(part => [part.heading, part.points ?? []] as const)),
);

const LADDER: Omit<StageMatrixExtraData, 'variableKeys'> = {
  stages: LADDER_STAGES,
  // Read off the explainer rather than typed again. The explainer exists to teach a reader the
  // ladder's columns, so a deck whose two pages name them differently teaches the wrong thing --
  // and two hand-kept lists of eight is exactly the pair that drifts.
  rungNames: EXPLAINER.panels.flatMap(panel => (panel.parts ?? []).map(part => part.heading ?? '')),
  totalLabel: 'Total Aided Awareness',
  totalExcludes: ['Unaware'],
  spectrum: SPECTRUM,
};

/** A fresh copy each time, so no two blocks share one. */
function copy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function exampleExplainer(): StageExplainerExtraData {
  return copy(EXPLAINER);
}

export function exampleLadder(): Omit<StageMatrixExtraData, 'variableKeys'> {
  return copy(LADDER);
}
